/**
 * Sysadmin Catalog → Agents (PR-K2).
 *
 * List view for system agent templates — the LAST and HEAVIEST catalog
 * leaf because Agents COMPOSE every other entity.
 *
 *   - filter chips (status, category, trigger mode)
 *   - search by name/slug/description
 *   - cursor-paginated table (Load more button)
 *   - "New Agent" button → multi-section create drawer
 *
 * Detail at /sysadmin/catalog/agents/$id (catalog.agents.$id.tsx).
 *
 * Lifecycle: every agent row carries a status pill (draft / published /
 * deprecated / retired). Tenants only see published-AND-active agents
 * via /v1/agents/system; tenants then CLONE into their own custom_agents
 * row via POST /v1/agents/clone.
 *
 * Mirrors catalog.skills.tsx + catalog.tools.tsx patterns. No mock
 * data. Empty list shows the empty state, errors show the error card.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Bot,
  Search,
  AlertTriangle,
  Plus,
  Loader2,
  Sparkles,
  Wrench,
  Code2,
  BookOpen,
  Workflow,
  MessageSquare,
  CalendarClock,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  useCatalogAgents,
  useCreateCatalogAgent,
  type AgentStatus,
} from '@/lib/use-sysadmin-catalog-agents';

// R5 P0-1/P0-2 fix (2026-05-13): list now lives under index route ID
// `/sysadmin/catalog/agents/`; parent `catalog.agents.tsx` is a pure
// layout with <Outlet/> so detail/new/builder children can render.
export const Route = createFileRoute('/sysadmin/catalog/agents/')({
  component: SysadminCatalogAgentsPage,
});

const STATUS_FILTERS: Array<{ key: AgentStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'deprecated', label: 'Deprecated' },
  { key: 'retired', label: 'Retired' },
];

const TRIGGER_FILTERS: Array<{
  key: 'all' | 'conversational' | 'scheduled' | 'event';
  label: string;
  icon: typeof MessageSquare;
}> = [
  { key: 'all', label: 'Any trigger', icon: MessageSquare },
  { key: 'conversational', label: 'Conversational', icon: MessageSquare },
  { key: 'scheduled', label: 'Scheduled', icon: CalendarClock },
  { key: 'event', label: 'Event', icon: Zap },
];

function statusPillClass(status: AgentStatus): string {
  switch (status) {
    case 'published':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    case 'draft':
      return 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30';
    case 'deprecated':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30';
    case 'retired':
      return 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-400 border-zinc-500/30';
  }
}

function SysadminCatalogAgentsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<AgentStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [triggerFilter, setTriggerFilter] = useState<
    'all' | 'conversational' | 'scheduled' | 'event'
  >('all');
  const [search, setSearch] = useState('');
  // Inline create drawer kept as a power-user shortcut. The primary
  // "New Agent" button now routes to the Argus Architect mode chooser
  // at /sysadmin/catalog/agents/new (per UI parity port 2026-05-11).
  const [showCreate, setShowCreate] = useState(false);

  const list = useCatalogAgents({
    status: statusFilter === 'all' ? undefined : statusFilter,
    category: categoryFilter || undefined,
    trigger: triggerFilter === 'all' ? undefined : triggerFilter,
    search: search.trim() || undefined,
    limit: 50,
  });

  const allItems = useMemo(
    () => list.data?.pages.flatMap((p) => p.items) ?? [],
    [list.data],
  );

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    allItems.forEach((a) => a.category && set.add(a.category));
    return Array.from(set).sort();
  }, [allItems]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Bot className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Agent Templates
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs font-mono">
              §3.3 / §6 / §8
            </Badge>
            <Badge variant="secondary" className="text-xs">
              v1
            </Badge>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Catalog</CardTitle>
          <CardDescription>
            Sysadmin-authored agent templates that compose model + prompt +
            tools + functions + skills + library + workflows + behavior +
            persona. Tenants only see agents with{' '}
            <span className="font-mono">status=published</span> via the
            redacted{' '}
            <span className="font-mono">/v1/agents/system</span>{' '}
            endpoint, then{' '}
            <span className="font-medium">CLONE</span> a tenant-scoped
            copy they can modify. Composition refs must themselves be
            published before this agent can publish.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Filters + search + new button */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Status:</span>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={cn(
                'px-2.5 py-1 rounded-full text-xs border transition-colors',
                statusFilter === f.key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border hover:border-foreground/40',
              )}
              title={`Filter by ${f.label.toLowerCase()}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground mr-1">Trigger:</span>
          {TRIGGER_FILTERS.map((f) => {
            const Icon = f.icon;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setTriggerFilter(f.key)}
                className={cn(
                  'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors',
                  triggerFilter === f.key
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border hover:border-foreground/40',
                )}
                title={`Filter by ${f.label.toLowerCase()}`}
              >
                <Icon className="h-3 w-3" />
                {f.label}
              </button>
            );
          })}
        </div>

        {categoryOptions.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground mr-1">
              Category:
            </span>
            <button
              type="button"
              onClick={() => setCategoryFilter('')}
              className={cn(
                'px-2.5 py-1 rounded-full text-xs border transition-colors',
                !categoryFilter
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border hover:border-foreground/40',
              )}
            >
              All
            </button>
            {categoryOptions.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategoryFilter(c)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs border transition-colors',
                  categoryFilter === c
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border hover:border-foreground/40',
                )}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search by name, slug, description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => setShowCreate(true)}
              title="Quick draft via the inline drawer"
            >
              <Plus className="h-3.5 w-3.5" /> Quick Draft
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => navigate({ to: '/sysadmin/catalog/agents/new' })}
              title="Create a new agent — choose Build with AI, Quick Create, or Import"
            >
              <Plus className="h-3.5 w-3.5" /> New Agent
            </Button>
          </div>
        </div>
      </div>

      {list.isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
            Loading agents…
          </CardContent>
        </Card>
      ) : list.error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {list.error.message}</div>
          </CardContent>
        </Card>
      ) : allItems.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No agents match the current filters.
            {(statusFilter !== 'all' ||
              categoryFilter ||
              triggerFilter !== 'all' ||
              search) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter('all');
                  setCategoryFilter('');
                  setTriggerFilter('all');
                  setSearch('');
                }}
                className="ml-2"
              >
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-32">Slug</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-28">Category</TableHead>
                  <TableHead className="w-20 text-right" title="Tools">
                    <Wrench className="h-3.5 w-3.5 inline" />
                  </TableHead>
                  <TableHead className="w-20 text-right" title="Functions">
                    <Code2 className="h-3.5 w-3.5 inline" />
                  </TableHead>
                  <TableHead className="w-20 text-right" title="Skills">
                    <Sparkles className="h-3.5 w-3.5 inline" />
                  </TableHead>
                  <TableHead className="w-20 text-right" title="Library refs">
                    <BookOpen className="h-3.5 w-3.5 inline" />
                  </TableHead>
                  <TableHead className="w-20 text-right" title="Workflows">
                    <Workflow className="h-3.5 w-3.5 inline" />
                  </TableHead>
                  <TableHead className="w-20 text-right">v</TableHead>
                  <TableHead className="w-24 text-right">Clones</TableHead>
                  <TableHead className="w-44">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allItems.map((a) => (
                  <TableRow
                    key={a.id}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    <TableCell>
                      <Link
                        to="/sysadmin/catalog/agents/$id"
                        params={{ id: a.id }}
                        className="block"
                      >
                        <div className="text-sm font-medium">
                          {a.displayName}
                        </div>
                        {a.description && (
                          <div className="text-[11px] text-muted-foreground line-clamp-1 max-w-md">
                            {a.description}
                          </div>
                        )}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/sysadmin/catalog/agents/$id"
                        params={{ id: a.id }}
                        className="font-mono text-xs"
                      >
                        {a.slug}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] capitalize border',
                          statusPillClass(a.status),
                        )}
                      >
                        {a.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">
                        {a.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {a.toolSlugs.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {a.functionKeys.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {a.skillSlugs.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {a.libraryRefs.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {a.workflowSlugs.length}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      v{a.version}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-right">
                      {a.cloneCount}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {new Date(a.updatedAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {list.hasNextPage && (
              <div className="border-t px-4 py-3 flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                      Loading more…
                    </>
                  ) : (
                    'Load more'
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CreateAgentSheet
        open={showCreate}
        onOpenChange={setShowCreate}
      />
    </div>
  );
}

// ─── Create drawer ────────────────────────────────────────────────────────

interface CreateDraft {
  slug: string;
  displayName: string;
  description: string;
  category: string;
  systemPrompt: string;
  personaPrompt: string;
}

const EMPTY_CREATE: CreateDraft = {
  slug: '',
  displayName: '',
  description: '',
  category: 'general',
  systemPrompt: '',
  personaPrompt: '',
};

interface CreateAgentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CreateAgentSheet({ open, onOpenChange }: CreateAgentSheetProps) {
  const [draft, setDraft] = useState<CreateDraft>(EMPTY_CREATE);
  const create = useCreateCatalogAgent();

  const isValid =
    draft.slug.trim().length > 0 &&
    /^[a-z0-9_.-]+$/.test(draft.slug.trim()) &&
    draft.displayName.trim().length > 0 &&
    draft.description.trim().length > 0 &&
    draft.systemPrompt.trim().length > 0;

  function reset() {
    setDraft(EMPTY_CREATE);
  }

  async function handleCreate() {
    if (!isValid) return;
    try {
      await create.mutateAsync({
        slug: draft.slug.trim(),
        displayName: draft.displayName.trim(),
        description: draft.description.trim(),
        category: draft.category.trim() || 'general',
        systemPrompt: draft.systemPrompt,
        personaPrompt: draft.personaPrompt.trim() || null,
      });
      toast.success(`Agent "${draft.slug}" created as draft.`);
      reset();
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to create agent: ${msg}`);
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <SheetContent className="w-[640px] sm:max-w-[640px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Create new agent
          </SheetTitle>
          <SheetDescription>
            Creates a draft agent. Compose the model, prompt, tools,
            functions, skills, library refs, workflows, behavior, and
            triggers in the detail page after creation. Publish requires
            every reference to itself be published.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div>
            <Label htmlFor="ag-slug">
              Slug <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ag-slug"
              placeholder="ask-argus"
              value={draft.slug}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  slug: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                })
              }
              className="mt-1.5 font-mono"
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              Lowercase letters, numbers, dot/dash/underscore. Stable
              identifier — tenants see this in the redacted view.
            </div>
          </div>

          <div>
            <Label htmlFor="ag-display">
              Display name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ag-display"
              placeholder="Ask Argus"
              value={draft.displayName}
              onChange={(e) =>
                setDraft({ ...draft, displayName: e.target.value })
              }
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="ag-desc">
              Description <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="ag-desc"
              placeholder="The conversational UX shell. Manages clarification, formatting, citations, and handoff to specialist agents."
              rows={2}
              value={draft.description}
              onChange={(e) =>
                setDraft({ ...draft, description: e.target.value })
              }
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="ag-cat">Category</Label>
            <Input
              id="ag-cat"
              placeholder="general"
              value={draft.category}
              onChange={(e) =>
                setDraft({ ...draft, category: e.target.value })
              }
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="ag-sys">
              System prompt <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="ag-sys"
              placeholder="You are Ask Argus, the conversational UX shell of the ArgusAI platform. Your job is to understand the user's intent, clarify when ambiguous, route to specialist agents, and format responses with citations."
              rows={6}
              value={draft.systemPrompt}
              onChange={(e) =>
                setDraft({ ...draft, systemPrompt: e.target.value })
              }
              className="mt-1.5 font-mono text-sm"
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              Deterministic instructions. Distinct from persona (the
              identity/tone preamble).
            </div>
          </div>

          <div>
            <Label htmlFor="ag-persona">Persona (optional)</Label>
            <Textarea
              id="ag-persona"
              placeholder="You are Ask Argus — calm, precise, citation-driven. You ask clarifying questions when uncertain."
              rows={3}
              value={draft.personaPrompt}
              onChange={(e) =>
                setDraft({ ...draft, personaPrompt: e.target.value })
              }
              className="mt-1.5"
            />
          </div>

          <div className="border-t pt-4 flex items-center gap-2">
            <Button
              onClick={handleCreate}
              disabled={!isValid || create.isPending}
              className="gap-1.5"
            >
              {create.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Creating…
                </>
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5" /> Create draft
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              disabled={create.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
